import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, ShieldCheck, Eye, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import api, { downloadOfferPdf, CANCELLED_OFFER } from "@/lib/api";
import { PortalNav } from "@/components/layout/PortalNav";
import { Loader } from "@/components/shared/Loader";
import { StatusBadge } from "@/components/shared/StatusBadge";

export default function ApplicationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);

  useEffect(() => {
    api.get(`/candidate/applications/${id}`).then((r) => setApp(r.data)).catch(() => navigate("/portal"));
  }, [id, navigate]);

  if (!app) return <div className="min-h-screen bg-[var(--aur-bg)]"><PortalNav /><Loader /></div>;

  const viewDoc = async (doc) => {
    try {
      const res = await api.get(`/candidate/documents/${doc.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { toast.error("Could not open document"); }
  };

  const stages = app.stages || [];
  const currentIdx = stages.indexOf(app.status);
  const rejected = app.status === "Rejected";

  return (
    <div className="min-h-screen bg-[var(--aur-bg)]">
      <PortalNav />
      <div className="mx-auto max-w-4xl px-6 py-12 lg:px-8">
        <button onClick={() => navigate("/portal")} className="mb-8 flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[var(--aur-text-2)] hover:text-gold">
          <ArrowLeft size={14} /> Back to portal
        </button>

        <div className="flex items-center gap-3">
          <h1 className="font-serif text-3xl tracking-tight sm:text-4xl">{app.job_title}</h1>
          <StatusBadge status={app.status} />
        </div>
        <p className="mt-2 font-mono text-[11px] uppercase tracking-widest text-[var(--aur-text-2)]">{app.reference} · {app.department} · {app.application_path} path</p>

        {/* Stage timeline */}
        <section className="mt-12">
          <p className="eyebrow">Application Progress</p>
          <div className="mt-8 flex items-center">
            {stages.map((s, i) => {
              const done = !rejected && i <= currentIdx;
              const active = !rejected && i === currentIdx;
              return (
                <div key={s} className="flex flex-1 items-center last:flex-none">
                  <div className="flex flex-col items-center">
                    <div className={`flex h-9 w-9 items-center justify-center rounded-full border-2 transition-all ${done ? "border-gold bg-gold text-[var(--aur-bg)]" : "border-[var(--aur-border)] text-[var(--aur-text-2)]"} ${active ? "ring-4 ring-gold/20" : ""}`}>
                      {done && i < currentIdx ? <Check size={16} /> : <span className="font-mono text-xs">{i + 1}</span>}
                    </div>
                    <span className={`mt-2 whitespace-nowrap font-sans text-[11px] ${done ? "text-gold" : "text-[var(--aur-text-2)]"}`}>{s}</span>
                  </div>
                  {i < stages.length - 1 && <div className={`mx-1 h-0.5 flex-1 ${i < currentIdx && !rejected ? "bg-gold" : "bg-[var(--aur-border)]"}`} />}
                </div>
              );
            })}
          </div>
          {rejected && <p className="mt-6 rounded-md border border-rose-500/30 bg-rose-500/10 px-4 py-3 font-sans text-sm text-rose-300">This application was not successful. We encourage you to apply for other roles.</p>}
        </section>

        {/* History */}
        <section className="mt-12">
          <p className="eyebrow">History</p>
          <div className="mt-6 space-y-4 border-l border-[var(--aur-border)] pl-6">
            {[...app.history].reverse().map((h, i) => (
              <div key={i} className="relative">
                <span className="absolute -left-[27px] top-1.5 h-2.5 w-2.5 rounded-full bg-gold" />
                <div className="flex items-center gap-3">
                  <StatusBadge status={h.status} />
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--aur-text-2)]">{new Date(h.at).toLocaleString()}</span>
                </div>
                {h.note && <p className="mt-1 font-sans text-sm text-[var(--aur-text-2)]">{h.note}</p>}
              </div>
            ))}
          </div>
        </section>

        {/* Documents */}
        {app.documents?.length > 0 && (
          <section className="mt-12">
            <p className="eyebrow">Submitted Documents</p>
            <div className="mt-5 space-y-2">
              {app.documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between rounded-md border border-[var(--aur-border)] bg-[var(--aur-card)] px-4 py-3">
                  <span className="flex items-center gap-2 font-sans text-sm"><FileText size={15} className="text-gold" />{d.original_filename} <span className="text-xs text-[var(--aur-text-2)]">({d.doc_type})</span></span>
                  <button onClick={() => viewDoc(d)} data-testid={`app-doc-view-${d.id}`} className="flex items-center gap-1.5 rounded-md border border-[var(--aur-border)] px-3 py-1.5 font-sans text-xs text-gold hover:bg-gold/10"><Eye size={13} /> View</button>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Compliance */}
        {app.compliance && (
          <section className="mt-12">
            <p className="eyebrow">Compliance Checklist</p>
            <p className="mt-2 font-sans text-sm text-[var(--aur-text-2)]">{app.compliance.template_name}</p>
            <div className="mt-5 space-y-2">
              {app.compliance.items.map((it) => (
                <div key={it.id} className="flex items-center justify-between rounded-md border border-[var(--aur-border)] bg-[var(--aur-card)] px-4 py-3">
                  <span className="font-sans text-sm text-[var(--aur-text)]">{it.label}</span>
                  <StatusBadge status={it.status} />
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Pre-offer requirements */}
        {app.offer_prep && app.status === "Selected" && (
          <section className="mt-12" data-testid="candidate-offer-prep">
            <p className="eyebrow">Pre-offer Requirements</p>
            <div className="mt-4 grid gap-4 rounded-md border border-gold/30 bg-gold/5 p-5 sm:grid-cols-2">
              <div><p className="font-mono text-[10px] uppercase tracking-widest text-[var(--aur-text-2)]">Proposed monthly salary</p><p className="mt-1 font-serif text-2xl text-gold" data-testid="candidate-prep-salary">SGD {app.offer_prep.salary}</p></div>
              <div><p className="font-mono text-[10px] uppercase tracking-widest text-[var(--aur-text-2)]">Complete medical examination by</p><p className="mt-1 font-serif text-2xl text-gold" data-testid="candidate-prep-medical">{new Date(`${app.offer_prep.medical_date}T00:00:00`).toLocaleDateString("en-SG", { dateStyle: "long" })}</p></div>
              <p className="font-sans text-sm text-[var(--aur-text-2)] sm:col-span-2">Please complete your medical examination and keep your passport / ID and certificates ready. Your official offer letter will be issued once these are done.</p>
            </div>
          </section>
        )}

        {/* Offer */}
        {app.offer && CANCELLED_OFFER.includes(app.offer.status) && (
          <section className="mt-12" data-testid="app-offer-cancelled">
            <p className="eyebrow flex items-center gap-2"><ShieldCheck size={13} /> Official Offer</p>
            <p className="mt-4 rounded-md border border-rose-500/30 bg-rose-500/10 px-4 py-3 font-sans text-sm text-rose-300">
              {app.offer.status === "Withdrawn" ? "The offer issued for this application has been withdrawn and is no longer valid." : "The offer for this application has been cancelled and is no longer valid."}
            </p>
          </section>
        )}
        {app.offer && !CANCELLED_OFFER.includes(app.offer.status) && app.offer.versions?.[0] && (
          <section className="mt-12">
            <p className="eyebrow flex items-center gap-2"><ShieldCheck size={13} /> Official Offer</p>
            <div className="mt-4 flex items-center gap-3">
              <StatusBadge status={app.offer.status} />
              <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--aur-text-2)]">Version {app.offer.current_version}</span>
              <button onClick={() => downloadOfferPdf(`/candidate/offers/${app.offer.id}/pdf`, `Offer_${app.job_title}.pdf`).catch(() => toast.error("Download failed"))} data-testid="app-offer-download" className="ml-auto flex items-center gap-1.5 rounded-full border border-gold/40 px-4 py-2 font-sans text-xs font-semibold uppercase tracking-widest text-gold hover:bg-gold/10"><Download size={13} /> Download PDF</button>
            </div>
            <div className="relative mt-4 overflow-hidden rounded-md border border-[var(--aur-border)] bg-white p-6 text-black" dangerouslySetInnerHTML={{ __html: app.offer.versions[0].html }} />
            <p className="mt-3 font-sans text-xs text-[var(--aur-text-2)]">Respond to this offer from the Offers tab in your portal.</p>
          </section>
        )}
      </div>
    </div>
  );
}
